import { useGetStructureId } from '@/hooks/useGetStructureId';
import { PointOfInterest } from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import { allVoiTemplates as allVoiTemplatesState } from '@/components/Analysis/state';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { checkIfVoi } from '@/utils/poi';
import { useViewPoiIn3D } from '../../Insights/InsightsExplorer/PointOfInterests/useViewPoiIn3D';

export const useReviewIn3D = ({
  pointOfInterest,
  currentViewpoint,
}: {
  pointOfInterest: PointOfInterest;
  currentViewpoint?: string;
}) => {
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);
  const structureId = useGetStructureId();
  const {
    id,
    type: poiType,
    assembly: { id: assemblyId, tagName },
    location,
    centerPoint3d,
  } = pointOfInterest;

  const { x, y, z } = centerPoint3d ?? {};
  const centerPoint3dExists = ![x, y, z].includes(undefined);

  const isUncontextualized = !centerPoint3dExists;
  const isVoi = checkIfVoi(allVoiTemplates, poiType);
  const imageViewExists = currentViewpoint ?? location?.name;

  const poiData = {
    id,
    assembly: tagName,
    assemblyId,
    imageName: currentViewpoint ?? location?.name ?? NOT_APPLICABLE_LABEL,
    imageLink: NOT_APPLICABLE_LABEL,
    isUncontextualized,
    fov: 0,
    centerPoint3d: centerPoint3dExists ? JSON.stringify([x, y, z]) : '',
  };

  if (!isUncontextualized) {
    const fov = isVoi ? 0 : imageViewExists ? 0 : 2;
    poiData.fov = fov;
    const lookAt = `&look.x=${x}&look.y=${y}&look.z=${z}`;
    const position = `&position.x=${imageViewExists ? x : 0}&position.y=${
      imageViewExists ? y : 0
    }&position.z=${imageViewExists ? z : 0}`;
    poiData.imageLink = `${window.location.origin}/analysis/${structureId}/viewer?assembly=${poiData.assembly}&poi=${poiData.id}${lookAt}${position}&fov=${fov}&showOnlySelectedPoi=true`;
    if (imageViewExists) poiData.imageLink += `&selectedSpherical=${poiData.imageName}`;
  }

  const { handleClick: handleViewIn3D } = useViewPoiIn3D({
    assemblyId,
    isUncontextualized,
    centerPoint3d: poiData.centerPoint3d,
    imageLink: poiData.imageLink,
    fov: poiData.fov,
  });

  return { handleViewIn3D, imageLink: poiData.imageLink };
};
